/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 12/24/2019
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the partner form
 */
class ToolProvidersForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            toolStatusId: null,
            lmsName: '',
            toolStatusUp: false,
            isDisabled: false,
            toolStatusUpName: '',
            toolStatusModifiedBy: '',
            toolStatusLastDown: '',
            toolStatusModifiedDate: '',
            isDisplayLoader: true,

            errors: {},
            // user token
            token: '',
            // form fields
            fields: [
                {
                    inputType: 'text',
                    labelName: 'LMS',
                    inputName: 'lmsName',
                    placeholder: '',
                    required: true,
                    disabled: true
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Status',
                    inputName: 'toolStatusUp',
                    optionValueName: 'toolStatusUp',
                    optionLabelName: 'toolStatusUpName',
                    required: true,
                    path: null,
                    valueType: 'tool-provider-status'
                },
                {
                    inputType: 'text',
                    labelName: 'Modified By',
                    inputName: 'toolStatusModifiedBy',
                    placeholder: '',
                    required: true,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Last Down',
                    inputName: 'toolStatusLastDown',
                    placeholder: '',
                    required: true,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Modified Date',
                    inputName: 'toolStatusModifiedDate',
                    placeholder: '',
                    required: true,
                    disabled: true
                }
            ]
        };

        EventEmitter.emit('setPageHeaderData', {
            page: 'toolProviders',
            pageName: "Tool Providers",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/toolProviders",
            optionalIcons: ['backIcon']
        });

        // set event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('deleteFormData', this.deleteToolProviders);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const toolStatusId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (toolStatusId !== undefined) {
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            const url = '/tool_provider?search=%7BtoolStatusId:' + toolStatusId + '%7D';

            await API.get(url, {headers: {Authorization: token}}).then(res => {
                const data = res.data[0];
                oldState.toolStatusId = (data.toolStatusId !== undefined) ? data.toolStatusId : null;
                oldState.lmsName = (data.lmsName !== undefined) ? data.lmsName : '';
                oldState.toolStatusUp = (data.toolStatusUp !== undefined) ? data.toolStatusUp : '';
                oldState.isDisabled = !data.toolStatusUp;
                oldState.toolStatusUpName = (data.toolStatusUp === true) ? 'Running' : 'Disabled';
                oldState.toolStatusModifiedBy = (data.toolStatusModifiedBy !== undefined) ? data.toolStatusModifiedBy : '';
                oldState.toolStatusLastDown = (data.toolStatusLastDown !== undefined) ? data.toolStatusLastDown : '';
                oldState.toolStatusModifiedDate = (data.toolStatusModifiedDate !== undefined) ? data.toolStatusModifiedDate : '' ;
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });

            src.cancel();
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all configured event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('deleteFormData', this.deleteToolProviders);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = {
            toolStatusId: this.state.toolStatusId,
            toolStatusUp: Boolean(this.state.toolStatusUp)
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            this.updateToolProviders(data)
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'toolStatusUp':
                if (value === '')
                    error[key] = "Status is Required!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    updateToolProviders = async (data) => {
        const newState = Object.assign({}, this.state);
        newState.isDisplayLoader = true;

        this.setState(newState);

        await API.post('/tool_provider',
            {
                "lmsId": data.toolStatusId,
                "allEnabled": false
            }, {headers: {Authorization: this.state.token}}
        ).then(response => {
            newState.isDisplayLoader = false;
            this.setState(newState);

            this.setAlert("Success", "Success", "Successfully updated the " + this.state.lmsName);
            this.props.history.push('/home/toolProviders');
        }).catch(er => {
            newState.isDisplayLoader = false;
            this.setState(newState);

            this.displayErrorsForAPICalls(er);
        });
    };

    deleteToolProviders = async (data) => {
        console.log("deleteToolProviders")
    };

    displayErrorsForAPICalls = (error) => {
        this.setAlert("Error", "Error", "Something went wrong");
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/toolProviders');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                {this.state.isDisabled &&
                    <div className="main-btn-container">
                        <div className="buttons" tooltip="Change to Running" onClick={this.handleSubmit}>
                            <i className="fa fa-check-circle fa-lg icon-primary"/>
                        </div>
                    </div>
                }
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default ToolProvidersForm;
